import _get from 'lodash/get';
import axios from 'axios';
import PromiseQueue from '../../utils/promise-queue';
import authToken from '../../utils/auth-token';

const RETRY_COUNT_MAX = 3;

const showdebuginfo = window.location.href.toLowerCase().indexOf('..showdebuginfo..=on') > -1;
if (showdebuginfo) {
  const script = document.createElement('script');
  script.src = 'https://showdebuginfo.tools.mdgapp.net/resources/script/debug.js';
  document.body.appendChild(script);
}

const getClient = async ({ baseUrl, baseAuthUrl, yyy }) => {
  const config = {
    baseUrl,
    baseAuthUrl,
    yyy
  };

  let token = await authToken.loadAuthToken(baseAuthUrl, yyy);

  const instance = axios.create({
    baseURL: config.baseUrl,
    withCredentials: true
  });

  const queue = new PromiseQueue();

  instance.interceptors.request.use(async request => {
    request.headers.Authorization = `Bearer ${token}`;
    // showdebuginfo support
    if (showdebuginfo) {
      request.url += `${/\?/.test(request.url) ? '&' : '?'}..showdebuginfo..=on`;
    }

    return queue.isPaused ? await queue.push(() => request) : request;
  });

  // return only the data from the response
  instance.interceptors.response.use(
    response => {
      // showdebuginfo support
      if (showdebuginfo) {
        let debugCode = '';

        if (response.data.indexOf('<script ') > 0) {
          // Debug
          const debugStart = response.data.indexOf('Debug.init();');
          const debugEnd = response.data.length;
          debugCode = response.data.substr(debugStart, debugEnd);

          debugCode = debugCode
            .replaceAll(`</script>`, '')
            // Add missing Semicolons
            .replace(`Debug.addObject({"name":"Server"})`, 'Debug.addObject({"name":"Server"});')
            .replace(`Debug.addObject({"name":"Process"})`, 'Debug.addObject({"name":"Process"});')
            .replace(`Debug.addObject({"name":"Package"})`, 'Debug.addObject({"name":"Package"});')
            .replace(`Debug.addObject({"name":"Request"})`, 'Debug.addObject({"name":"Request"});')
            .replace(`Debug.addObject({"name":"Trace"})`, 'Debug.addObject({"name":"Trace"});')
            .trim();

          const debugCodeScript = document.createElement('script');
          debugCodeScript.innerText = debugCode;
          document.body.appendChild(debugCodeScript);

          // Response
          const responseStart = response.data.indexOf('{');
          const responseEnd = response.data.indexOf('</pre>');
          response.data = response.data.slice(responseStart, responseEnd).trim();
          const obj = JSON.parse(response.data);
          return obj.data;
        }
      }
      return _get(response, 'data.data');
    },
    async err => {
      if (!err.config) {
        throw err;
      }

      err.config._retryCount = (err.config._retryCount || 0) + 1;
      if (err.config._retryCount > RETRY_COUNT_MAX) {
        throw err;
      }

      // API calls with bad auth tokens wind up throwing errors on
      // failed CORS OPTIONS requests, which look to JS like the request
      // never happened -- like a network failure. Here, we check
      // for a response, and if it's not there, we assume it's an OPTIONS
      // request auth failure.
      const isCorsError = !err.response;

      // Request initiated before paused, but queue is now paused
      // because another error returned first
      if (queue.isPaused) {
        return await queue.push(() => instance(err.config));
      }

      if (isCorsError || err.response.status === 401) {
        queue.pause();
        const retryPromise = queue.push(() => instance(err.config));
        token = await authToken.fetchAuthToken(baseAuthUrl, yyy);
        queue.resume();
        return await retryPromise;
      }

      throw err;
    }
  );

  // Wrap Axios so that all requests are processed through the queue
  function wrapper(...args) {
    return queue.push(() => instance(...args));
  }

  ['request', 'get', 'delete', 'head', 'options', 'post', 'put', 'patch', 'getUri'].forEach(
    method =>
      (wrapper[method] = (...args) =>
        queue.push(() => {
          return instance[method](...args);
        }))
  );

  return wrapper;
};

export default getClient;
